import React, { useState, useRef } from 'react';
import { Container, Header, Segment, Form, Button, Message } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { submitRSVP } from '../../services/rsvpService';
import './styles.css';

const heroImage = `${process.env.PUBLIC_URL}/imgs/hero3.jpg`;

function RSVP() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    nombre: '',
    asistencia: '',
    transporte: '',
    acompanantes: '',
    restricciones: '',
    guestDetails: [],
    password: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [errorField, setErrorField] = useState(null);

  const nombreRef    = useRef(null);
  const asistenciaRef = useRef(null);
  const transporteRef = useRef(null);
  const passwordRef  = useRef(null);
  const guestRefs    = useRef([]);

  const handleChange = (e, { name, value }) => {
    if (name === errorField) setErrorField(null);

    // Si cambia a "no asistencia", resetear campos relacionados
    if (name === 'asistencia' && value === 'no') {
      setFormData({ 
        ...formData, 
        asistencia: value,
        transporte: '',
        acompanantes: '',
        restricciones: '',
        guestDetails: []
      });
      return;
    }
    
    // Validar el número máximo de acompañantes
    if (name === 'acompanantes') {
      const numGuests = parseInt(value) || 0;
      // Limitar el valor a máximo 5
      if (numGuests > 5) {
        value = '5';
      }
      // No permitir valores negativos
      if (numGuests < 0) {
        value = '0';
      }
      
      const finalNumGuests = parseInt(value) || 0;
      const newGuestDetails = Array.from({ length: finalNumGuests }, (_, index) => ({
        nombre: formData.guestDetails[index]?.nombre || '',
        restricciones: formData.guestDetails[index]?.restricciones || '',
        isChild: formData.guestDetails[index]?.isChild || false
      }));
      setFormData({ ...formData, acompanantes: value, guestDetails: newGuestDetails });
      return;
    }
    
    setFormData({ ...formData, [name]: value });
  };

  const handleGuestChange = (index, field, value) => {
    if (field === 'nombre' && errorField === `guest_${index}`) setErrorField(null);
    const newGuestDetails = [...formData.guestDetails];
    newGuestDetails[index] = {
      ...newGuestDetails[index],
      [field]: value
    };
    setFormData({ ...formData, guestDetails: newGuestDetails });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nombre.trim()) {
      setErrorField('nombre');
      nombreRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (!formData.asistencia) {
      setErrorField('asistencia');
      asistenciaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (formData.asistencia === 'si' && !formData.transporte) {
      setErrorField('transporte');
      transporteRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const emptyGuest = formData.guestDetails.findIndex(g => !g.nombre.trim());
    if (emptyGuest !== -1) {
      setErrorField(`guest_${emptyGuest}`);
      guestRefs.current[emptyGuest]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (!formData.password.trim()) {
      setErrorField('password');
      passwordRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setLoading(true);
    setError(null);
    setErrorField(null);

    const result = await submitRSVP(formData);

    setLoading(false);

    if (result.success) {
      setSubmitted(true);
    } else {
      if (result.message && result.message.toLowerCase().includes('contraseña')) {
        setError(t('rsvp.wrongPassword'));
        setErrorField('password');
        passwordRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        setError(result.message);
      }
    }

    // Scroll to top to show the success/error message
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div>
      {/* Hero Section */}
      <Segment
        inverted
        textAlign='center'
        className="rsvp-hero-segment"
        vertical
      >
        {/* Background image with blur */}
        <div className="rsvp-hero-background" style={{ backgroundImage: `url(${heroImage})` }} />
        
        {/* Dark overlay */}
        <div className="rsvp-hero-overlay" />
        
        <Container text className="rsvp-hero-container">
          <Header
            as='h1'
            inverted
            className="rsvp-hero-title"
          >
            {t('rsvp.title')}
          </Header>
          <p className="rsvp-hero-subtitle">
            {t('rsvp.subtitle')}
          </p>
        </Container>
      </Segment>

      {/* Form Section */}
      <div className="rsvp-wrapper">
        <Segment className="rsvp-segment" vertical>
          <Container className="rsvp-container">

            {submitted && (
              <Message positive>
                <Message.Header>{t('rsvp.successTitle')}</Message.Header>
                <p>{t('rsvp.successMessage')}</p>
              </Message>
            )}

            {error && (
              <Message negative>
                <Message.Header>{t('rsvp.errorTitle')}</Message.Header>
                <p>{error}</p>
              </Message>
            )}

            <Segment>
              <Form onSubmit={handleSubmit}>
                <div ref={nombreRef}>
                <Form.Input
                  fluid
                  label={t('rsvp.fullName')}
                  placeholder={t('rsvp.fullNamePlaceholder')}
                  name='nombre'
                  value={formData.nombre}
                  onChange={handleChange}
                  error={errorField === 'nombre'}
                  disabled={submitted}
                />
                {errorField === 'nombre' && <p style={{ color: '#9f3a38', marginTop: '0.25rem', fontSize: '0.875em' }}>{t('rsvp.nombreRequired')}</p>}
                </div>

                <div ref={asistenciaRef}>
                <Form.Field error={errorField === 'asistencia'}>
                  <label>{t('rsvp.willAttend')}</label>
                  <Form.Radio
                    label={t('rsvp.willAttendYes')}
                    value='si'
                    name='asistencia'
                    checked={formData.asistencia === 'si'}
                    onChange={(e, { value }) => { setErrorField(null); setFormData({ ...formData, asistencia: value }); }}
                    disabled={submitted}
                  />
                  <Form.Radio
                    label={t('rsvp.willAttendNo')}
                    value='no'
                    name='asistencia'
                    checked={formData.asistencia === 'no'}
                    onChange={(e, { value }) => { setErrorField(null); setFormData({ ...formData, asistencia: value }); }}
                    disabled={submitted}
                  />
                  {errorField === 'asistencia' && <p style={{ color: '#9f3a38', marginTop: '0.5rem', fontSize: '0.875em' }}>{t('rsvp.asistenciaRequired')}</p>}
                </Form.Field>
                </div>

                {formData.asistencia === 'si' && (
                  <div ref={transporteRef}>
                  <Form.Field error={errorField === 'transporte'}>
                    <label>{t('rsvp.transport')}</label>
                    <Form.Radio
                      label={t('rsvp.transportPrivate')}
                      value='privado'
                      checked={formData.transporte === 'privado'}
                      onChange={handleChange}
                      name='transporte'
                      disabled={submitted}
                    />
                    <Form.Radio
                      label={t('rsvp.transportBus')}
                      value='bus'
                      checked={formData.transporte === 'bus'}
                      onChange={handleChange}
                      name='transporte'
                      disabled={submitted}
                    />
                    {errorField === 'transporte' && <p style={{ color: '#9f3a38', marginTop: '0.5rem', fontSize: '0.875em' }}>{t('rsvp.transportRequired')}</p>}
                  </Form.Field>
                  </div>
                )}

                <Form.Input
                  fluid
                  label={t('rsvp.guests')}
                  placeholder={t('rsvp.guestsPlaceholder')}
                  name='acompanantes'
                  type='number'
                  min='0'
                  max='5'
                  value={formData.acompanantes}
                  onChange={handleChange}
                  disabled={formData.asistencia === 'no' || submitted}
                />

                {/* Campos dinámicos para cada acompañante */}
                {formData.asistencia !== 'no' && formData.guestDetails.map((guest, index) => (
                  <div key={index} ref={el => guestRefs.current[index] = el} style={{ marginTop: '1.5rem', padding: '1.5rem', backgroundColor: 'transparent', borderRadius: '10px', border: '1px solid rgba(34,36,38,.15)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <Header as='h4' style={{ margin: 0, color: '#1a1a1a' }}>
                        {t('rsvp.companion')} {index + 1}
                      </Header>
                      <Form.Checkbox
                        toggle
                        label={t('rsvp.guestIsChild')}
                        checked={guest.isChild || false}
                        onChange={(e, { checked }) => handleGuestChange(index, 'isChild', checked)}
                        disabled={submitted}
                      />
                    </div>
                    <Form.Group widths='equal'>
                      <Form.Input
                        fluid
                        label={t('rsvp.guestName')}
                        placeholder={t('rsvp.guestNamePlaceholder')}
                        value={guest.nombre}
                        onChange={(e, { value }) => handleGuestChange(index, 'nombre', value)}
                        error={errorField === `guest_${index}`}
                        disabled={submitted}
                      />
                      <Form.TextArea
                        label={t('rsvp.guestRestrictions')}
                        placeholder={t('rsvp.guestRestrictionsPlaceholder')}
                        value={guest.restricciones}
                        onChange={(e, { value }) => handleGuestChange(index, 'restricciones', value)}
                        rows={1}
                        style={{ minHeight: '38px', resize: 'vertical' }}
                        disabled={submitted}
                      />
                    </Form.Group>
                    {errorField === `guest_${index}` && <p style={{ color: '#9f3a38', marginTop: '0.25rem', fontSize: '0.875em' }}>{t('rsvp.guestNameRequired', { num: index + 1 })}</p>}
                  </div>
                ))}

                <Form.Field style={{ marginTop: '1rem' }}>
                <Form.TextArea
                  label={t('rsvp.restrictions')}
                  placeholder={t('rsvp.restrictionsPlaceholder')}
                  name='restricciones'
                  value={formData.restricciones}
                  onChange={handleChange}
                  disabled={formData.asistencia === 'no' || submitted}
                />
                </Form.Field>

                <div ref={passwordRef}>
                <Form.Input
                  fluid
                  label={t('rsvp.password')}
                  placeholder={t('rsvp.passwordPlaceholder')}
                  name='password'
                  type='password'
                  value={formData.password}
                  onChange={handleChange}
                  error={errorField === 'password'}
                  icon='lock'
                  iconPosition='left'
                  disabled={submitted}
                />
                {errorField === 'password' && <p style={{ color: '#9f3a38', marginTop: '0.25rem', fontSize: '0.875em' }}>{t('rsvp.passwordRequired')}</p>}
                </div>

                <Button
                  type='submit'
                  size='large'
                  fluid
                  className="rsvp-submit-button"
                  loading={loading}
                  disabled={loading || submitted}
                >
                  {t('rsvp.submitButton')}
                </Button>
              </Form>
            </Segment>
          </Container>
        </Segment>
      </div>
    </div>
  );
}

export default RSVP;
